import { useCaptainProfile, useMyFleet, usePastSeasons, type PastSeasonBoard } from '@/api/hooks'
import { useAuth } from '@/auth/useAuth'
import { Link } from 'react-router-dom'
import { ReputationCompass } from '@/components/ReputationCard'
import { PrestigeHint } from '@/components/PrestigeHint'
import {
  PrestigeIcon, AnchorIcon, IslandIcon, GoldIcon,
  CombatIcon, MapIcon, BuildingIcon, DiplomacyIcon, PennantIcon, TrophyIcon,
} from '@/components/game/GameIcons'
import { ReputationIcon } from '@/components/game/ReputationIcon'
import { CaptainPortrait } from '@/components/game/cosmetics/CaptainPortrait'
import { CaptainBlason } from '@/components/game/cosmetics/CaptainBlason'
import { StatTile, Badge } from '@/components/game/ui'
import { InstallAppButton } from '@/pwa/InstallAppButton'
import { usePwaInstall } from '@/pwa/usePwaInstall'

/** Warm accent per faction — tints the hero emblem ring. */
const FACTION_RING: Record<string, string> = {
  marine: 'border-ocean-300/60',
  guilde: 'border-sand-300/60',
  corsaire: 'border-sand-500/60',
  pirate: 'border-rum-500/60',
  neutral: 'border-ocean-500/50',
}

/**
 * /capitaine — the captain's "livret de bord": identity, emergent faction
 * (reputation compass), prestige and a snapshot of their holdings. Read-only,
 * fed by /api/me (useAuth) + /api/me/fleet (useMyFleet, deduped with the banner).
 */
export function Captain() {
  const { user } = useAuth()
  const { data: fleet } = useMyFleet()
  const { data: profile } = useCaptainProfile()
  const { canInstall, isStandalone, isIosSafari } = usePwaInstall()

  if (!user) return null

  // Même condition que le guard interne d'InstallAppButton : on ne montre la
  // section « Emporter Corsalia » que si le bouton a réellement quelque chose à
  // proposer, sinon on laisserait un titre orphelin sans action.
  const canShowInstall = !isStandalone && (canInstall || isIosSafari)

  const rep = user.reputation
  const ring = (rep && FACTION_RING[rep.faction]) || FACTION_RING.neutral
  const memberSince = new Date(user.createdAt).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
  const isAdmin = user.roles?.includes('ROLE_ADMIN')

  const shipCount = fleet?.ships.filter((s) => s.status !== 'lost').length ?? null
  const islandName = fleet?.island?.name ?? null

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-16 space-y-7">
      {/* ——— livret de bord : identité + réputation réunies dans une seule bannière ——— */}
      <section className="relative overflow-hidden rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/45 p-7 sm:p-8">
        {/* faint compass watermark */}
        <div className="pointer-events-none absolute -right-10 -top-12 w-56 h-56 opacity-[0.06]" aria-hidden="true">
          <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
            <g stroke="#E5CC8F"><circle cx="100" cy="100" r="92" strokeWidth="0.6" /><circle cx="100" cy="100" r="60" strokeWidth="0.5" /></g>
            <g transform="rotate(0 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
            <g transform="rotate(90 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
            <g transform="rotate(180 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
            <g transform="rotate(270 100 100)"><path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" /><path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" /></g>
          </svg>
        </div>

        {/* Agencement mobile B « carte d'identité » : rangée compacte médaillon
            (68px) + colonne identité alignée en HAUT ; en dessous, blason et
            prestige en deux tuiles jumelles ; puis bouton Cabine pleine
            largeur. Desktop (sm:+) : bannière flex historique inchangée. */}
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-start sm:items-center gap-4 sm:gap-5 min-w-0 sm:flex-1">
            {/* pavillon emblem — portrait peint si configuré à la cabine, sinon
                médaillon de rang. Cliquable vers la cabine (plume au survol). */}
            <Link
              to="/capitaine/cabine"
              aria-label="Modifier mon apparence"
              // Portrait peint : pas de cadre carré (le médaillon porte déjà son
              // anneau + cadre de cabine — retour utilisateur T18) ; l'affordance
              // de survol passe par la plume + un léger scale. Fallback : cadre
              // carré historique autour du médaillon de rang.
              className={`group relative grid place-items-center w-[68px] h-[68px] sm:w-24 sm:h-24 shrink-0 ${
                profile?.appearance?.avatar
                  ? 'transition-transform hover:scale-[1.03]'
                  : `rounded-2xl bg-ocean-900/60 border-2 p-1 sm:p-1.5 hover:border-sand-300 transition-colors ${ring}`
              }`}
            >
              {profile?.appearance?.avatar
                ? <CaptainPortrait config={profile.appearance.avatar} size={88} className="w-full h-full" />
                : rep
                  ? <ReputationIcon faction={rep.faction} rank={rep.rank} size={88} className="w-full h-full" title={rep.title} />
                  : <AnchorIcon className="w-10 h-10 text-sand-300" />}
              {/* plume d'édition — n'apparaît qu'au survol du médaillon */}
              <span
                aria-hidden="true"
                className="absolute -bottom-1.5 -right-1.5 grid place-items-center w-6 h-6 rounded-full bg-ocean-900 border border-sand-300/60 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-sand-300" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19.5 4.5c-4.5.4-8.3 2.8-10.3 6.8L6.5 16l6.7-2.7c4-2 6-5.8 6.3-8.8Z" />
                  <path d="M6.5 16 4 20" />
                </svg>
              </span>
            </Link>

            {/* blason équipé — l'autre moitié de l'identité visuelle (hybride
                portrait/blason). Desktop uniquement : en mobile il vit dans la
                tuile jumelle « Blason » plus bas (petit bloc dupliqué, assumé). */}
            {profile?.appearance?.blason && (
              <div className="hidden sm:block shrink-0 self-center" title="Blason du capitaine">
                <CaptainBlason config={profile.appearance.blason} size={64} />
              </div>
            )}

            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-1 sm:mb-1.5">Livret de bord</p>
              <h1 className="font-display text-2xl sm:text-5xl text-sand-300 leading-none break-words">{user.pseudo}</h1>
              {rep && <p className="font-display text-base sm:text-lg text-sand-100 mt-1.5 sm:mt-2">{rep.title}</p>}
              <div className="mt-2.5 sm:mt-3 flex items-center gap-2 flex-wrap">
                {rep && <Badge>{rep.factionLabel}</Badge>}
                {/* Titre ÉQUIPÉ à la cabine — remplace les titres auto (streak). */}
                {profile?.appearance?.titleLabel && <Badge>{profile.appearance.titleLabel}</Badge>}
                {isAdmin && <Badge tone="rum">Amirauté</Badge>}
                <Badge tone="ghost">Membre depuis le {memberSince}</Badge>
              </div>
              {/* bouton Cabine — version desktop (inline sous les badges) ;
                  en mobile c'est la rangée pleine largeur en bas de la carte. */}
              <Link
                to="/capitaine/cabine"
                className="hidden sm:inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl border border-sand-300/40 bg-sand-300/10 text-sand-200 hover:bg-sand-300/20 transition-colors text-sm"
              >
                Cabine du capitaine
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          {/* prestige — boîte desktop ; en mobile : tuile jumelle plus bas. */}
          <div className="hidden sm:block group/tip relative cursor-help self-auto shrink-0 text-center px-5 py-3 rounded-xl bg-ocean-900/50 border border-sand-500/20">
            <PrestigeIcon className="w-7 h-7 mx-auto text-sand-300" />
            <p className="font-display text-3xl text-sand-100 tabular-nums mt-1">{user.prestigePoints}</p>
            <p className="text-[10px] uppercase tracking-wider text-ocean-300">Prestige</p>
            <PrestigeHint placement="bottom" />
          </div>
        </div>

        {/* Rangée 2 (mobile) — tuiles jumelles blason + prestige, moitié-moitié. */}
        <div className="relative mt-4 grid grid-cols-2 gap-3 sm:hidden">
          <div className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-ocean-900/50 border border-sand-500/20 px-3 py-3">
            {profile?.appearance?.blason ? (
              <CaptainBlason config={profile.appearance.blason} size={52} />
            ) : (
              <span className="grid place-items-center w-[52px] h-[52px]" aria-hidden="true">
                <PennantIcon className="w-7 h-7 text-ocean-300/50" />
              </span>
            )}
            <p className="text-[10px] uppercase tracking-wider text-ocean-300">Blason</p>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 rounded-xl bg-ocean-900/50 border border-sand-500/20 px-3 py-3 text-center">
            <PrestigeIcon className="w-6 h-6 text-sand-300" />
            <p className="font-display text-2xl text-sand-100 tabular-nums leading-none">{user.prestigePoints}</p>
            <p className="text-[10px] uppercase tracking-wider text-ocean-300">Prestige</p>
          </div>
        </div>

        {/* Rangée 3 (mobile) — entrée Cabine pleine largeur, impossible à rater. */}
        <Link
          to="/capitaine/cabine"
          className="relative sm:hidden mt-3 flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-sand-300/40 bg-sand-300/10 text-sand-200 hover:bg-sand-300/20 transition-colors text-sm"
        >
          Cabine du capitaine
          <span aria-hidden="true">→</span>
        </Link>

        {/* lien discret vers la vitrine publique (ce que voient les autres capitaines) */}
        <div className="relative mt-3 text-center sm:text-left">
          <Link
            to={`/capitaine/${encodeURIComponent(user.pseudo)}`}
            className="inline-flex items-center gap-1 text-xs text-ocean-300 hover:text-sand-300 transition-colors"
          >
            Voir mon profil public
            <span aria-hidden="true">→</span>
          </Link>
        </div>

        {/* réputation émergente — repliée dans la même bannière (plus de bandeau séparé) */}
        {rep && (
          <div className="relative mt-7 pt-6 border-t border-sand-500/15">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="min-w-0 sm:flex-1">
                <p className="text-[11px] uppercase tracking-[0.25em] text-ocean-300">Réputation émergente</p>
                <p className="text-sm text-ocean-200 mt-1.5 max-w-md">{rep.description}</p>
                <p className="text-xs text-ocean-300/80 mt-2 max-w-md">
                  Ton pavillon n'est jamais choisi dans un menu : ce sont tes <em>actes</em> (commerce, raids,
                  alliances) qui déplacent ta position sur les axes Légal/Hors-la-loi et Marchand/Guerrier.
                </p>
                <Link
                  to="/negociations"
                  className="mt-3 inline-flex items-center gap-2 text-sm text-sand-300 hover:text-sand-100 transition-colors"
                >
                  <DiplomacyIcon className="w-4 h-4" />
                  La table des négociations
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
              <div className="self-center sm:self-auto shrink-0">
                <ReputationCompass reputation={rep} />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ——— registre du bord ——— */}
      <div>
        <h2 className="font-display text-lg text-sand-100 mb-3">Registre du bord</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={<IslandIcon className="w-6 h-6 text-sand-300" />} value={islandName ?? '—'} label="Repaire" />
          <StatTile icon={<AnchorIcon className="w-6 h-6 text-sand-300" />} value={shipCount ?? '—'} label="Navires à flot" />
        </div>
      </div>

      {/* ——— faits d'armes (season deeds, from /api/me/profile) ——— */}
      {profile && (
        <div>
          <h2 className="font-display text-lg text-sand-100 mb-3">
            Faits d'armes{profile.season ? ` · ${profile.season.name}` : ''}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatTile icon={<CombatIcon className="w-6 h-6 text-sand-300" />} value={profile.stats.raidsConducted} label="Raids menés" />
            <StatTile icon={<GoldIcon className="w-6 h-6 text-sand-300" />} value={profile.stats.tradeRunsConducted} label="Expéditions commerciales" />
            <StatTile icon={<MapIcon className="w-6 h-6 text-sand-300" />} value={profile.stats.islandsDiscovered} label="Îles découvertes" />
            <StatTile icon={<AnchorIcon className="w-6 h-6 text-sand-300" />} value={profile.stats.shipsBuilt} label="Navires construits" />
            <StatTile icon={<BuildingIcon type="hq" className="w-6 h-6 text-sand-300" />} value={profile.stats.hqLevel} label="Niveau QG" />
            <StatTile icon={<BuildingIcon type="shipyard" className="w-6 h-6 text-sand-300" />} value={profile.stats.shipyardLevel} label="Niveau chantier" />
          </div>
        </div>
      )}

      {/* ——— mes places au classement : les podiums gravés des saisons closes ——— */}
      <CaptainPalmares pseudo={user.pseudo} />

      {/* ——— installer l'app ——— */}
      {canShowInstall && (
        <div>
          <h2 className="font-display text-lg text-sand-100 mb-3">Emporter Corsalia</h2>
          <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <p className="min-w-0 flex-1 text-sm text-ocean-200">
              Installe Corsalia sur ton appareil pour la retrouver d'un geste, plein écran, comme une app.
            </p>
            <InstallAppButton variant="card" />
          </div>
        </div>
      )}

      {/* ——— identité ——— */}
      <div>
        <h2 className="font-display text-lg text-sand-100 mb-3">Identité</h2>
        <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 divide-y divide-sand-500/10">
          <Row label="Pseudo de capitaine" value={user.pseudo} />
          <Row label="Adresse de courrier" value={user.email} />
          <Row label="Faction actuelle" value={rep ? rep.factionLabel : '—'} />
          <Row label="Membre depuis" value={memberSince} />
        </div>
      </div>
    </div>
  )
}

const PLACE_LABEL: Record<number, string> = { 1: '1ʳᵉ place', 2: '2ᵉ place', 3: '3ᵉ place' }

/**
 * Le score d'une place gravée, dans la même grammaire que les classements : une
 * ressource se lit en ICÔNE, jamais en toutes lettres.
 */
function PlaceScore({ metric, value }: { metric: PastSeasonBoard['metric']; value: number }) {
  return (
    <span data-place-score className="inline-flex items-baseline gap-1 shrink-0">
      {metric === 'gold' && <GoldIcon className="w-4 h-4 text-sand-300 shrink-0 self-center" />}
      {metric === 'prestige' && <PrestigeIcon className="w-4 h-4 text-sand-300 shrink-0 self-center" />}
      <span className="font-display tabular-nums text-sand-100 text-sm">{value.toLocaleString('fr-FR')}</span>
      {metric === 'pts' && <span className="text-[10px] uppercase tracking-wider text-ocean-300">pts</span>}
      {metric === 'paliers' && (
        <span className="text-[10px] uppercase tracking-wider text-ocean-300">{value > 1 ? 'paliers' : 'palier'}</span>
      )}
    </span>
  )
}

/**
 * Les places de podium du capitaine sur les saisons closes, REGROUPÉES PAR
 * SAISON : c'est ainsi qu'on s'en souvient (« la saison où j'ai fini deuxième du
 * négoce »), pas board par board.
 *
 * Alimenté par le MÊME palmarès public que l'onglet « Saisons passées » des
 * classements (TanStack déduplique la requête sur la clé), filtré sur son
 * pseudo. Rien ne s'affiche tant qu'il n'a rien gravé : une section vide
 * vaudrait un reproche, et un livret de bord ne reproche rien.
 */
function CaptainPalmares({ pseudo }: { pseudo: string }) {
  const { data } = usePastSeasons()

  // L'ordre des saisons est celui du serveur (la plus récente d'abord) ; une
  // saison où le capitaine n'a rien gravé ne laisse pas de sous-bloc vide.
  const seasons = (data?.seasons ?? [])
    .map((s) => ({
      id: s.id,
      name: s.name,
      places: s.boards.flatMap((b) =>
        b.rows
          .filter((r) => r.pseudo === pseudo)
          .map((r) => ({ key: `${b.key}-${r.rank}`, board: b.label, metric: b.metric, rank: r.rank, score: r.score })),
      ),
    }))
    .filter((s) => s.places.length > 0)

  if (seasons.length === 0) return null

  return (
    <div>
      <h2 className="font-display text-lg text-sand-100 mb-3">Mes places au classement</h2>
      <div className="space-y-3">
        {seasons.map((s) => (
          <div
            key={s.id}
            data-palmares-season={s.id}
            className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40"
          >
            <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-2.5">
              <span className="font-display text-sand-200 truncate">{s.name}</span>
              <span className="text-[11px] uppercase tracking-wider text-ocean-300 tabular-nums shrink-0">
                {s.places.length} place{s.places.length > 1 ? 's' : ''}
              </span>
            </div>
            <ul className="border-t border-sand-500/10 divide-y divide-sand-500/10">
              {s.places.map((p) => (
                <li key={p.key} className="flex items-center justify-between gap-4 px-5 py-3">
                  <span className="flex items-center gap-2.5 min-w-0">
                    <TrophyIcon className="w-4 h-4 text-sand-300 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm text-sand-200 truncate">{p.board}</span>
                      <span className="block text-[11px] uppercase tracking-wider text-ocean-300">
                        {PLACE_LABEL[p.rank] ?? `${p.rank}ᵉ place`}
                      </span>
                    </span>
                  </span>
                  <PlaceScore metric={p.metric} value={p.score} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-ocean-300 mt-2">
        Gravé à la clôture de la saison. Le titre qui va avec s'équipe dans la cabine du capitaine.
      </p>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3.5">
      <span className="text-sm text-ocean-300">{label}</span>
      <span className="text-sm text-sand-200 font-medium truncate">{value}</span>
    </div>
  )
}
